import React, { useState } from 'react'
import * as Yup from 'yup'

import { compose } from 'recompose'
import { useSnackbar } from 'notistack'
import { withNamespaces } from 'react-i18next'

import MuiFab from '@material-ui/core/Fab'
import MuiPaper from '@material-ui/core/Paper'
import MuiPopover from '@material-ui/core/Popover'
import { withStyles } from '@material-ui/core/styles'
import MuiTypography from '@material-ui/core/Typography'
import MuiIconEmoticonOutlined from '@material-ui/icons/InsertEmoticonOutlined'

import WidgetFeedbackRatingForm from './WidgetFeedbackRatingForm'

import AvaliacaoSatisfacaoSistemaService from 'services/avaliacao-satisfacao-sistema'

import styled from 'styled-components'

import theme from 'app/theme'
import { withRestErrorHandler } from 'app/utils'
import { finishLoading, startLoading } from 'store/helpers'

import MuiIconButton from '@material-ui/core/IconButton'
import MuiIconClose from '@material-ui/icons/CloseOutlined'

const Paper = styled(MuiPaper)`
  && {
    display: flex;
    align-items: center;
    flex-direction: column;
    box-shadow: 0px 6px 20px rgba(165, 214, 167, 0.2);
    ${theme.breakpoints.up(400 + theme.spacing(3) * 2)} {
      width: 360px;
    }
  }
`

const Title = styled(MuiTypography)`
  && {
    color: ${theme.palette.common.white};
    font-weight: 400;
  }
`

const Header = styled(MuiPaper)`
  && {
    width: 100%;
    padding: ${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(3)}px ${theme.spacing(3.25)}px;
    background-color: ${theme.palette.primary.main};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const CloseButtonContainer = styled(MuiIconButton)`
  && {
    margin-left: auto;
    padding: 4px;
    color: ${theme.palette.common.white};
  }
`

const styles = () => ({
  colorBadge: {
    backgroundColor: theme.palette.info.main
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:active': {
      color: 'inherit'
    },
    marginTop: 7
  },
  fab: {
    position: 'fixed',
    bottom: 33,
    borderRadius: '30px 8px 30px 30px',
    width: 74,
    height: 32
  }
})

const WidgetFeedbackRating = ({ t, classes, tRestError, shouldTranslateX }) => {
  const [anchorFab, setAnchorFab] = useState(null)
  const [popoverOpened, setPopoverOpened] = useState(false)
  const [ratingMessage, setRatingMessage] = useState(null)
  const [selectedEmoji, setSelectedEmoji] = useState(null)

  const { enqueueSnackbar } = useSnackbar()

  const ratingMessageValidationSchema = Yup.object().shape({
    ratingMessage: Yup.string()
      .nullable()
      .max(500, t('global_validation_number_max_length'))
  })

  const ratingMessageInitialValues = {
    ratingMessage: null
  }

  const handlePopoverOpen = () => {
    setPopoverOpened(true)
  }

  const handlePopoverClose = () => {
    setPopoverOpened(false)
    setSelectedEmoji(null)
    setRatingMessage(null)
  }

  const handleRatingMessage = (key, value, setValues) => {
    setRatingMessage(value)
    setValues({
      [key]: value
    })
  }

  const handleRatingSubmit = async (_, { setSubmitting }) => {
    try {
      startLoading()

      const params = {
        nivelSatisfacao: selectedEmoji,
        comentario: ratingMessage
      }

      const { ok, data } = await AvaliacaoSatisfacaoSistemaService.sendFeedbackRating(params)

      if (!ok) {
        return enqueueSnackbar(tRestError(data && data.message), { variant: 'error' })
      }

      enqueueSnackbar(t('widget_feedback_rating_popover_success_message'), { variant: 'success' })
      handlePopoverClose()
    } catch (err) {
      enqueueSnackbar(t('global_error_message'), { variant: 'error' })
    } finally {
      finishLoading()
      setSubmitting(false)
    }
  }

  const handleRatingEmoji = value => {
    setSelectedEmoji(value)
  }

  const emojiList = [
    {
      value: 1,
      emoji: '😡',
      label: t('widget_feedback_rating_popover_emojis_label.awful')
    },
    {
      value: 2,
      emoji: '🙁',
      label: t('widget_feedback_rating_popover_emojis_label.bad')
    },
    {
      value: 3,
      emoji: '😐',
      label: t('widget_feedback_rating_popover_emojis_label.indifferent')
    },
    {
      value: 4,
      emoji: '🙂',
      label: t('widget_feedback_rating_popover_emojis_label.good')
    },
    {
      value: 5,
      emoji: '😀',
      label: t('widget_feedback_rating_popover_emojis_label.excellent')
    }
  ]

  return (
    <>
      <MuiFab
        color="primary"
        onClick={handlePopoverOpen}
        className={classes.fab}
        style={{ right: shouldTranslateX ? 192 : 110, zIndex: 9999 }}
      >
        <span className={classes.link} ref={setAnchorFab} rel="noopener noreferrer">
          <MuiIconEmoticonOutlined />
        </span>
      </MuiFab>

      <MuiPopover
        open={popoverOpened}
        anchorEl={anchorFab}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Paper>
          <Header elevation={0}>
            <CloseButtonContainer id="btn-fechar" onClick={handlePopoverClose}>
              <MuiIconClose />
            </CloseButtonContainer>
            <Title variant="h6" id="context-title">
              {t('widget_feedback_rating_popover_title')}
            </Title>
          </Header>

          <WidgetFeedbackRatingForm
            t={t}
            rows="6"
            rowsMax="6"
            multiline
            characterLimit={500}
            inputName="ratingMessage"
            handleRatingMessage={handleRatingMessage}
            validationSchema={ratingMessageValidationSchema}
            initialValues={ratingMessageInitialValues}
            onFormSubmit={handleRatingSubmit}
            onRatingEmoji={handleRatingEmoji}
            selectedEmoji={selectedEmoji}
            emojiList={emojiList}
          />
        </Paper>
      </MuiPopover>
    </>
  )
}

export default compose(withStyles(styles), withNamespaces('translation'), withRestErrorHandler())(WidgetFeedbackRating)
